import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { Flame, Check, ArrowRight, Trophy, MessageSquare, Loader2, Share2, ImageDown } from "lucide-react";
import { API } from "../lib/api";
import axios from "axios";
import { useRoastShare, ShareCardPortal } from "../hooks/useRoastShare";

const fileUrl = (p) => (p ? `${API}/labs/files/${p}` : null);

export default function BrandChallenge() {
  const { slug } = useParams();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [picked, setPicked] = useState(null);
  const [comment, setComment] = useState("");
  const [results, setResults] = useState(null);
  const [voting, setVoting] = useState(false);
  const [ageBand, setAgeBand] = useState("");
  const [city, setCity] = useState("");
  const [postAge, setPostAge] = useState("");
  const [postCity, setPostCity] = useState("");
  const [demoDone, setDemoDone] = useState(false);
  const { share, busyId, shareRef, shareItem } = useRoastShare();

  useEffect(() => {
    axios.get(`${API}/labs/campaigns/${slug}`)
      .then((r) => { setC(r.data); if (localStorage.getItem(`labs_voted_${slug}`)) loadResults(); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, [slug]);

  const loadResults = async () => {
    try { setResults((await axios.get(`${API}/labs/campaigns/${slug}/results`)).data); } catch (e) {}
  };

  const submit = async () => {
    if (!picked) return toast.error("Pick one first!");
    setVoting(true);
    try {
      const r = await axios.post(`${API}/labs/campaigns/${slug}/vote`, { option_id: picked, comment, age_band: ageBand || null, city: city.trim() || null });
      localStorage.setItem(`labs_voted_${slug}`, "1");
      setResults(r.data);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't record your vote"); }
    finally { setVoting(false); }
  };

  const addDemographics = async () => {
    if (!postAge && !postCity.trim()) return;
    try {
      await axios.post(`${API}/labs/campaigns/${slug}/vote/${results.your_vote_id}/demographics`, { age_band: postAge || null, city: postCity.trim() || null });
      setDemoDone(true);
      toast.success("Thanks — added to the breakdown!");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't add"); }
  };

  const shareChallenge = async () => {
    const url = window.location.href;
    const text = c ? `${c.brand_name} wants your take: ${c.question}` : "Vote on this";
    try {
      if (navigator.share) { await navigator.share({ title: c?.brand_name, text, url }); return; }
    } catch (e) { if (e?.name === "AbortError") return; }
    try { await navigator.clipboard.writeText(url); toast.success("Link copied"); }
    catch (e) { toast.error("Couldn't share"); }
  };

  const shareResultCard = () => {
    if (!results) return;
    const wpct = (results.options.find((o) => o.id === results.winner_id) || {}).pct || 0;
    share({ kind: "labs_result", brand: c.brand_name, question: c.question, winner: results.winner_label,
      winner_pct: wpct, participants: results.participants, top_comment: results.comments?.[0]?.comment || null }, "labs_result");
  };

  if (loading) return <div className="min-h-screen grain flex items-center justify-center"><Loader2 className="animate-spin text-brand" size={28} /></div>;
  if (notFound) return (
    <div className="min-h-screen grain flex items-center justify-center text-center px-4">
      <div><div className="font-heading font-bold text-3xl">Challenge not found</div><p className="text-zinc-500 mt-2">This link may have expired.</p></div>
    </div>
  );

  const isReaction = c.format === "reaction_poll";
  const voted = !!results;

  return (
    <div className="min-h-screen grain">
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2 text-xs tracking-[0.3em] uppercase text-zinc-500 mb-6">
          <Flame size={14} className="text-brand" /> RoastLit <span className="text-brand">Labs</span>
        </div>

        <div className="flex items-center gap-3 mb-2">
          {c.brand_logo_path && <img src={fileUrl(c.brand_logo_path)} alt={c.brand_name} className="w-10 h-10 rounded-full object-cover border border-white/10" data-testid="brand-logo" />}
          <span className="text-brand-accent font-bold uppercase tracking-widest text-sm" data-testid="brand-name">{c.brand_name}</span>
        </div>
        {c.headline && <div className="text-zinc-400 text-sm mb-1">{c.headline}</div>}
        <h1 className="font-heading font-bold uppercase tracking-tighter text-3xl sm:text-4xl" data-testid="campaign-question">{c.question}</h1>
        <p className="text-zinc-500 text-sm mt-2">Help {c.brand_name} decide — one tap, no account needed.</p>

        {isReaction && (c.subject_image_path || c.subject_label) && (
          <div className="mt-6 rounded-lg border border-white/10 bg-zinc-900/60 p-5 text-center">
            {c.subject_image_path && <img src={fileUrl(c.subject_image_path)} alt="subject" className="max-h-72 mx-auto rounded-md object-contain" data-testid="subject-image" />}
            {c.subject_label && <div className="mt-3 font-bold">{c.subject_label}</div>}
          </div>
        )}

        {/* Voting / Results */}
        <div className={`mt-6 grid gap-3 ${isReaction ? "sm:grid-cols-2" : "sm:grid-cols-3"}`} data-testid="campaign-options">
          {c.options.map((o) => {
            const res = voted ? results.options.find((x) => x.id === o.id) : null;
            const isWinner = voted && results.winner_id === o.id;
            return (
              <button key={o.id} data-testid={`option-${o.id}`} disabled={voted}
                onClick={() => setPicked(o.id)}
                className={`relative overflow-hidden text-left rounded-lg border p-4 transition-all ${picked === o.id && !voted ? "border-brand bg-brand/10" : isWinner ? "border-brand-accent bg-brand-accent/5" : "border-white/10 bg-zinc-900/60 hover:border-white/30"}`}>
                {voted && <div className="absolute inset-0 bg-brand/10" style={{ width: `${res?.pct || 0}%`, transition: "width .7s" }} />}
                {o.image_path && <img src={fileUrl(o.image_path)} alt={o.label} className="relative w-full h-32 object-cover rounded-md mb-3" />}
                <div className="relative flex items-center justify-between">
                  <span className="font-bold">{o.label}</span>
                  {picked === o.id && !voted && <Check size={16} className="text-brand" />}
                  {isWinner && <Trophy size={16} className="text-brand-accent" />}
                </div>
                {voted && <div className="relative text-sm text-zinc-400 mt-1" data-testid={`option-pct-${o.id}`}>{res?.pct}% · {res?.count} vote{res?.count === 1 ? "" : "s"}</div>}
              </button>
            );
          })}
        </div>

        {!voted && (
          <>
            {c.allow_comments && (
              <textarea data-testid="campaign-comment" value={comment} onChange={(e) => setComment(e.target.value)} maxLength={280} rows={2}
                placeholder="Why? (optional — one line)" className="mt-4 w-full bg-zinc-950 border border-zinc-800 rounded-sm p-3 text-sm focus:border-brand focus:outline-none resize-none" />
            )}
            <details className="mt-3 group" data-testid="campaign-demographics">
              <summary className="text-xs text-zinc-500 cursor-pointer hover:text-zinc-300 select-none">+ Add your details (optional, anonymous)</summary>
              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap gap-2">
                  {["<18", "18-24", "25-34", "35-44", "45+"].map((b) => (
                    <button key={b} type="button" data-testid={`age-${b}`} onClick={() => setAgeBand(ageBand === b ? "" : b)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${ageBand === b ? "border-brand bg-brand/10 text-white" : "border-white/15 text-zinc-400 hover:border-white/30"}`}>{b}</button>
                  ))}
                </div>
                <input data-testid="city-input" value={city} onChange={(e) => setCity(e.target.value)} maxLength={40}
                  placeholder="City (optional)" className="w-full bg-zinc-950 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none" />
                <div className="text-[11px] text-zinc-600">Only ever shown to the brand as anonymous group totals — never linked to you.</div>
              </div>
            </details>
            <button data-testid="campaign-vote-btn" onClick={submit} disabled={voting}
              className="mt-4 w-full py-3.5 rounded-full bg-brand hover:bg-brand-hover text-black font-bold transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2">
              {voting ? <Loader2 size={18} className="animate-spin" /> : <>Lock in my pick <ArrowRight size={18} /></>}
            </button>
          </>
        )}

        {voted && (
          <div className="mt-6" data-testid="campaign-results">
            <div className="flex items-center gap-2 text-sm text-zinc-400"><Check size={16} className="text-tier-mild" /> Thanks! You're 1 of <b className="text-white">{results.participants}</b>. <b className="text-brand-accent ml-1">{results.winner_label}</b> is winning.</div>
            <button data-testid="campaign-share-btn" onClick={shareChallenge}
              className="mt-4 inline-flex items-center gap-2 rounded-full border border-white/15 hover:border-brand/50 text-zinc-200 font-bold text-sm px-4 py-2.5 transition-colors">
              <Share2 size={15} /> Share this challenge
            </button>
            <button data-testid="campaign-recap-btn" onClick={shareResultCard} disabled={busyId === "labs_result"}
              className="mt-4 ml-2 inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-sm px-4 py-2.5 transition-colors disabled:opacity-60">
              <ImageDown size={15} /> {busyId === "labs_result" ? "Preparing…" : "Share the result"}
            </button>

            {results.your_vote_id && !results.your_demographics_given && !demoDone && (
              <div data-testid="postvote-demo-prompt" className="mt-5 rounded-lg border border-white/10 bg-zinc-900/60 p-4">
                <div className="text-sm font-semibold text-zinc-200">Quick one — help {c.brand_name} read the room?</div>
                <div className="text-xs text-zinc-500 mb-3">Totally optional & anonymous. Shown only as group totals.</div>
                <div className="flex flex-wrap gap-2 mb-3">
                  {["<18", "18-24", "25-34", "35-44", "45+"].map((b) => (
                    <button key={b} type="button" data-testid={`postage-${b}`} onClick={() => setPostAge(postAge === b ? "" : b)}
                      className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${postAge === b ? "border-brand bg-brand/10 text-white" : "border-white/15 text-zinc-400 hover:border-white/30"}`}>{b}</button>
                  ))}
                </div>
                <div className="flex gap-2">
                  <input data-testid="postcity-input" value={postCity} onChange={(e) => setPostCity(e.target.value)} maxLength={40} placeholder="City (optional)"
                    className="flex-1 bg-zinc-950 border border-zinc-800 rounded-sm p-2.5 text-sm focus:border-brand focus:outline-none" />
                  <button data-testid="postvote-demo-submit" onClick={addDemographics} disabled={!postAge && !postCity.trim()}
                    className="px-4 py-2 rounded-full bg-brand hover:bg-brand-hover text-black font-bold text-sm transition-colors disabled:opacity-40">Add</button>
                </div>
              </div>
            )}
            {demoDone && <div className="mt-4 flex items-center gap-2 text-sm text-tier-mild" data-testid="postvote-demo-done"><Check size={15} /> Added to the breakdown — thanks!</div>}
            {results.comments?.length > 0 && (
              <div className="mt-5">
                <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-zinc-500 mb-2"><MessageSquare size={13} /> What people said</div>
                <div className="space-y-2">
                  {results.comments.slice(0, 6).map((cm, i) => (
                    <div key={i} className="rounded-sm border border-white/10 bg-zinc-900/60 px-3 py-2 text-sm">
                      <span className="text-zinc-300">"{cm.comment}"</span> <span className="text-zinc-600 text-xs">— {cm.option_label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {c.funnel_prompt && (
              <a href="/" data-testid="campaign-funnel" className="mt-6 flex items-center justify-between gap-3 rounded-lg border border-brand/30 bg-gradient-to-br from-brand/10 to-transparent p-5 hover:border-brand/60 transition-colors">
                <div>
                  <div className="font-heading font-bold text-lg">Want to be judged next?</div>
                  <div className="text-sm text-zinc-400">RoastLit lets real humans roast your bio, pitch or look. See how you'd score.</div>
                </div>
                <ArrowRight className="text-brand shrink-0" />
              </a>
            )}
          </div>
        )}

        <div className="mt-10 text-center text-[11px] text-zinc-600">Powered by RoastLit Labs · anonymous & aggregated · no prizes, just opinions</div>
      </div>
      <ShareCardPortal shareRef={shareRef} item={shareItem} />
    </div>
  );
}
